/**
 * Цены ПРО (месяц, год, навсегда) в валюте иностранца — для карточки ПРО
 * на /pricing астрофил-ру (astrofeel.ru).
 *
 * Источник — GET /api/stripe/pro-prices?currency=…: на бэкенде тот же
 * get_checkout_price, по которому создаётся Stripe Checkout, поэтому в
 * карточке ровно та сумма и валюта, что спишет Stripe (включая запасную
 * USD/EUR, если в нужной валюте цены нет).
 *
 * Запрос уходит ТОЛЬКО при enabled (иностранец на ру, STRIPE_PRIMARY).
 * На рф и у посетителя из России хук ничего не делает.
 *
 * Возвращает { status: 'off' | 'loading' | 'ready' | 'error', plans }.
 */
import { useEffect, useState } from 'react';
import { BACKEND_URL as API_URL } from '@/lib/backendUrl';

// Один ответ на валюту на всю вкладку. На главной цены ПРО нужны сразу
// двум блокам (WhyAstrofilBlock и PricingBlock), и раньше каждый слал свой
// запрос. Теперь второй берёт уже летящий или пришедший ответ. Неудачу не
// запоминаем: следующий монтаж попробует снова.
const cache = new Map();

function loadProPrices(currency) {
  if (!cache.has(currency)) {
    const p = fetch(`${API_URL}/api/stripe/pro-prices?${new URLSearchParams({ currency })}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.plans && Object.keys(data.plans).length) return data.plans;
        throw new Error('no plans');
      });
    p.catch(() => { if (cache.get(currency) === p) cache.delete(currency); });
    cache.set(currency, p);
  }
  return cache.get(currency);
}

/** Только для тестов: сбросить запомненные ответы между кейсами. */
export function resetStripeProPricesCache() {
  cache.clear();
}

export function useStripeProPrices(enabled, currency) {
  const [state, setState] = useState({ status: enabled ? 'loading' : 'off', plans: null });
  useEffect(() => {
    if (!enabled || !currency) {
      setState({ status: 'off', plans: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', plans: null });
    loadProPrices(currency)
      .then((plans) => { if (!cancelled) setState({ status: 'ready', plans }); })
      .catch(() => { if (!cancelled) setState({ status: 'error', plans: null }); });
    return () => { cancelled = true; };
  }, [enabled, currency]);
  return state;
}

export default useStripeProPrices;
